// app/src/features/tracking/useTrackingSettingsWiring.js
import { useState } from "react";
import { usePersistedState, JSON_CODEC } from "../../hooks/usePersistedState.js";
import { useSession } from "../../hooks/useSession.js";
import { useTrackingPrefs } from "../../hooks/useTrackingPrefs.js";
import { TRACKING_PREFS_LS_KEY } from "../../lib/tracking.js";

/**
 * The tracking wiring's second point: the per-forecast alert prefs cache (`trackingPrefs`, read
 * by nothing but the prefs hook), the prefs hook itself (`useTrackingPrefs`, its 7-field argument
 * object passed unchanged) and which forecast's settings flyover is open. Called where `AppInner`
 * called `useTrackingPrefs`: after the `forecastsById` memo (a boot/share-built lookup the hook
 * resolves rows in — an input here, as `pinnedRef` / `setPinned` are `useTrackingWiring`'s),
 * before the engine selection.
 *
 * `onSharedAccessLost` is the arrow `AppInner` builds (`(_id, message) => noticeShown(message)`):
 * `noticeShown` is declared after this point, and the deferred read is what makes the call legal
 * there — the arrow comes in, never `noticeShown` itself.
 *
 * @param {object} deps
 * @param {Map} deps.forecastsById  owned and shared rows by id (AppInner's memo)
 * @param {React.MutableRefObject} deps.pinnedRef  the watchlist mirror (useTrackingWiring's)
 * @param {Function} deps.setPinned  the watchlist setter (useTrackingWiring's)
 * @param {Function} deps.onSharedAccessLost  told when a write is refused because the share is gone
 */
export function useTrackingSettingsWiring({
  forecastsById,
  pinnedRef,
  setPinned,
  onSharedAccessLost,
}) {
  const { authEnabled } = useSession();
  // Per-forecast ALERT prefs (thrIdx, inapp, email): this localStorage map (keyed by forecast id)
  // is the client cache. The email opt-in and threshold are also persisted server-side:
  // patchTrackingPrefs PATCHes { notify: { email, thresholdPct } } and getTrackingPrefs layers the
  // server `forecast.notify` over this cache (server wins), so alerts fire from the notifier
  // service, not just the browser. `inapp` stays local-only.
  //
  // The cadence (cadIdx) is NOT persisted here: it derives from the server `forecast.schedule` via
  // specToCadIdx, and applySchedule is the single writer that PATCHes schedule (see
  // getTrackingPrefs / applySchedule below). A stale `cadIdx`/alert in old localStorage blobs is
  // always overridden by the server-sourced value in getTrackingPrefs.
  const [trackingPrefs, setTrackingPrefs] = usePersistedState(
    TRACKING_PREFS_LS_KEY,
    {},
    JSON_CODEC,
  );
  const { getTrackingPrefs, applySchedule, toggleWatchlist, patchTrackingPrefs } = useTrackingPrefs(
    {
      forecastsById,
      trackingPrefs,
      setTrackingPrefs,
      pinnedRef,
      setPinned,
      authEnabled,
      onSharedAccessLost,
    },
  );
  const [trackingSettingsId, setTrackingSettingsId] = useState(null);
  return {
    getTrackingPrefs,
    applySchedule,
    toggleWatchlist,
    patchTrackingPrefs,
    trackingSettingsId,
    setTrackingSettingsId,
  };
}
